import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiBookOpen,
  FiCalendar,
  FiClock,
  FiDollarSign,
  FiSettings,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiPlus,
  FiTrash2,
  FiEdit3,
  FiMapPin,
  FiGlobe,
  FiUsers,
  FiTag,
  FiAlertCircle,
  FiInfo,
  FiSave,
  FiLoader,
  FiHelpCircle,
  FiCreditCard,
  FiSmartphone,
  FiMail,
  FiMessageCircle,
  FiPhone,
  FiBell,
  FiCheckCircle,
  FiFileText,
  FiAward,
  FiTarget,
  FiList,
} from "react-icons/fi";
import { workshopApi } from "../../services/workshopApi";
import {
  validateWorkshopForm,
  validatePaymentInstructions,
  getPopularCategories,
  getResearchFields,
  formatDateForInput,
} from "../../utils/workshopUtils";
import "./WorkshopCreationForm.css";

const WorkshopCreationForm = ({
  workshopId = null,
  initialData = null,
  onSubmit,
  onCancel,
  isEditMode = false,
}) => {
  const navigate = useNavigate();
  
  // ============ STATE ============
  const [currentStep, setCurrentStep] = useState(1);
  const [submitSuccess, setSubmitSuccess] = useState(null); // {type: 'published'|'draft', workshop: {...}}
  const totalSteps = 5;

  // ============ FORM STATE - Matches Backend API Schema ============
  const [formData, setFormData] = useState({
    // Basic Info (Required)
    title: "",                      // 5-150 chars
    description: "",                // 20-2000 chars
    category: "",                   // 2-100 chars
    field: "",                      // 2-100 chars (research field) - REQUIRED
    tags: [],                       // Max 10 tags, each max 50 chars
    
    // NEW: Metadata fields
    language: "English",            // Default: English
    difficultyLevel: "all-levels",  // "beginner" | "intermediate" | "advanced" | "all-levels"
    prerequisites: [],              // Array of prerequisite strings
    learningOutcomes: [],           // What participants will learn
    targetAudience: "",             // Who should join
    certificateOffered: false,      // Show certificate badge
    certificateDetails: "",         // Certificate description (if offered)
    
    // Format (Required)
    format: "online",               // "online" | "physical" only (no hybrid in backend)
    
    // Location (structure per backend)
    location: {
      // For online:
      platform: "",                 // e.g., "Zoom", "Google Meet"
      meetingLink: "",              // Required for online when publishing
      // For physical:
      venue: "",                    // Required for physical
      address: "",
      city: "",
      country: "",
    },
    
    // Duration (Required)
    duration: {
      days: 1,                      // 1-30 days
      startDate: "",                // ISO8601, must be future
      endDate: "",                  // ISO8601, after startDate
    },
    
    // Capacity
    capacity: {
      limit: 30,                    // 1-1000
      unlimited: false,
    },
    
    // Pricing (Required)
    pricing: {
      type: "free",                 // "free" | "paid"
      amount: 0,                    // Required if paid (0-100,000)
      currency: "PKR",              // "PKR" | "USD" | "EUR" | "GBP"
    },
    
    // Payment Instructions (for paid workshops) - Backend structure
    paymentInstructions: {
      bankDetails: {
        accountTitle: "",
        accountNumber: "",
        bankName: "",
        iban: "",
      },
      mobilePayment: {
        easypaisa: { accountNumber: "", accountTitle: "" },
        jazzcash: { accountNumber: "", accountTitle: "" },
      },
      internationalPayment: {
        paypal: { email: "" },
        wise: { email: "" },
      },
      additionalInstructions: "",   // Max 1000 chars
      contactInfo: {
        whatsapp: "",
        email: "",
      },
    },
    
    // Settings
    settings: {
      allowQuestions: true,         // default: true
      requireApproval: true,        // default: true (backend default)
      sendReminders: true,          // default: true
      autoApprovePayments: false,   // default: false
    },
    
    // Visibility
    visibility: "public",           // "public" | "private" | "unlisted"
    
    ...initialData,
  });

  // NEW: State for array inputs
  const [prerequisiteInput, setPrerequisiteInput] = useState("");
  const [outcomeInput, setOutcomeInput] = useState("");
  
  const [schedule, setSchedule] = useState([]);
  const [errors, setErrors] = useState({});
  const [sessionErrors, setSessionErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [tagInput, setTagInput] = useState("");

  const categories = useMemo(() => getPopularCategories(), []);
  const researchFields = useMemo(() => getResearchFields(), []);

  // ============ EFFECTS ============
  useEffect(() => {
    if (workshopId && isEditMode) {
      loadWorkshopData();
    }
  }, [workshopId, isEditMode]);

  // Auto-calculate end date based on start date and duration
  useEffect(() => {
    if (formData.duration.startDate && formData.duration.days) {
      const start = new Date(formData.duration.startDate);
      const end = new Date(start);
      // For duration.days: end date is (days - 1) days after start, but at end of day
      // e.g., 1-day workshop starting Dec 16 00:00 → ends Dec 16 23:59
      // e.g., 2-day workshop starting Dec 16 00:00 → ends Dec 17 23:59
      end.setDate(end.getDate() + parseInt(formData.duration.days) - 1);
      end.setHours(23, 59, 0, 0); // Set to end of day to ensure endDate > startDate
      setFormData((prev) => ({
        ...prev,
        duration: {
          ...prev.duration,
          endDate: formatDateForInput(end),
        },
      }));
    }
  }, [formData.duration.startDate, formData.duration.days]);

  // ============ DATA LOADING ============
  const loadWorkshopData = async () => {
    setLoading(true);
    try {
      const response = await workshopApi.getWorkshop(workshopId);
      if (response.success) {
        setFormData(response.data);
        if (response.data.schedule) {
          setSchedule(response.data.schedule);
        }
      }
    } catch (error) {
      setErrors({ general: "Failed to load workshop data" });
    } finally {
      setLoading(false);
    }
  };

  // ============ HANDLERS ============
  const handleInputChange = useCallback((path, value) => {
    setFormData((prev) => {
      const keys = path.split(".");
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
    setErrors((prev) => ({ ...prev, [path]: null }));
  }, []);

  const handleTagAdd = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      handleInputChange("tags", [...formData.tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const handleTagRemove = (tag) => {
    handleInputChange(
      "tags",
      formData.tags.filter((t) => t !== tag)
    );
  };

  const validateCurrentStep = () => {
    const stepErrors = {};
    switch (currentStep) {
      case 1:
        // Title: 5-150 chars
        if (!formData.title.trim()) {
          stepErrors.title = "Title is required";
        } else if (formData.title.trim().length < 5) {
          stepErrors.title = "Title must be at least 5 characters";
        } else if (formData.title.trim().length > 150) {
          stepErrors.title = "Title cannot exceed 150 characters";
        }
        // Description: 20-2000 chars
        if (!formData.description.trim()) {
          stepErrors.description = "Description is required";
        } else if (formData.description.trim().length < 20) {
          stepErrors.description = "Description must be at least 20 characters";
        } else if (formData.description.trim().length > 2000) {
          stepErrors.description = "Description cannot exceed 2000 characters";
        }
        // Category: 2-100 chars (required)
        if (!formData.category || formData.category.trim().length < 2) {
          stepErrors.category = "Category is required";
        }
        // Field (research field): 2-100 chars (REQUIRED by backend)
        if (!formData.field || formData.field.trim().length < 2) {
          stepErrors.field = "Research field is required";
        }
        // Tags: max 10, each max 50 chars
        if (formData.tags.length > 10) {
          stepErrors.tags = "Maximum 10 tags allowed";
        }
        break;
      case 2:
        // Start date must be in future
        if (!formData.duration.startDate) {
          stepErrors.startDate = "Start date is required";
        } else {
          const startDate = new Date(formData.duration.startDate);
          if (startDate <= new Date()) {
            stepErrors.startDate = "Start date must be in the future";
          }
        }
        // Duration: 1-30 days
        if (!formData.duration.days || formData.duration.days < 1) {
          stepErrors.days = "Duration must be at least 1 day";
        } else if (formData.duration.days > 30) {
          stepErrors.days = "Duration cannot exceed 30 days";
        }
        // Location validation based on format
        if (formData.format === "physical") {
          if (!formData.location.venue?.trim()) {
            stepErrors.venue = "Venue is required for physical workshops";
          }
        }
        if (formData.format === "online") {
          // Meeting link required for online (except drafts - will be validated on publish)
          if (!formData.location.meetingLink?.trim()) {
            stepErrors.meetingLink = "Meeting link is required for online workshops";
          } else {
            // Validate URL format
            try {
              new URL(formData.location.meetingLink);
            } catch {
              stepErrors.meetingLink = "Please enter a valid URL";
            }
          }
        }
        break;
      case 3:
        // Capacity: 1-1000
        if (!formData.capacity.unlimited) {
          if (!formData.capacity.limit || formData.capacity.limit < 1) {
            stepErrors.capacity = "Capacity must be at least 1";
          } else if (formData.capacity.limit > 1000) {
            stepErrors.capacity = "Capacity cannot exceed 1000";
          }
        }
        if (formData.pricing.type === "paid") {
          // Amount: > 0, max 100,000
          if (!formData.pricing.amount || formData.pricing.amount <= 0) {
            stepErrors.pricingAmount = "Price is required for paid workshops";
          } else if (formData.pricing.amount > 100000) {
            stepErrors.pricingAmount = "Price cannot exceed 100,000";
          }
          // Validate payment instructions
          const paymentValidation = validatePaymentInstructionsForBackend(
            formData.paymentInstructions
          );
          if (!paymentValidation.isValid) {
            stepErrors.payment = paymentValidation.message;
          }
        }
        break;
      case 4:
        if (schedule.length === 0)
          stepErrors.schedule = "At least one day with sessions is required";
        if (hasSessionErrors())
          stepErrors.sessions = "Fix session errors before continuing";
        break;
      default:
        break;
    }
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const goToStep = (step) => {
    if (step < currentStep || validateCurrentStep()) {
      setCurrentStep(step);
    }
  };

  // ============ SCHEDULE MANAGEMENT (Backend format: schedule[].sessions[]) ============
  // Backend expects: { day: number, date: Date, sessions: [{ startTime, endTime, topic, agenda? }] }
  
  const generateScheduleTemplate = () => {
    if (!formData.duration.startDate || !formData.duration.days) return;
    const newSchedule = [];
    const startDate = new Date(formData.duration.startDate);
    
    for (let i = 0; i < parseInt(formData.duration.days); i++) {
      const sessionDate = new Date(startDate);
      sessionDate.setDate(sessionDate.getDate() + i);
      newSchedule.push({
        id: `day-${Date.now()}-${i}`,
        day: i + 1,
        date: sessionDate.toISOString(),
        sessions: [
          {
            id: `session-${Date.now()}-${i}-0`,
            startTime: "10:00",
            endTime: "12:00",
            topic: `Day ${i + 1} Morning Session`,
            agenda: "",
          }
        ],
      });
    }
    setSchedule(newSchedule);
  };

  const addDayToSchedule = () => {
    const lastDay = schedule[schedule.length - 1];
    const newDate = lastDay
      ? new Date(new Date(lastDay.date).getTime() + 86400000)
      : new Date(formData.duration.startDate);
    
    const newDay = {
      id: `day-${Date.now()}`,
      day: schedule.length + 1,
      date: newDate.toISOString(),
      sessions: [
        {
          id: `session-${Date.now()}-0`,
          startTime: "10:00",
          endTime: "12:00",
          topic: `Day ${schedule.length + 1} Session`,
          agenda: "",
        }
      ],
    };
    setSchedule([...schedule, newDay]);
  };

  const addSessionToDay = (dayId) => {
    setSchedule((prev) =>
      prev.map((day) => {
        if (day.id === dayId) {
          return {
            ...day,
            sessions: [
              ...day.sessions,
              {
                id: `session-${Date.now()}`,
                startTime: "14:00",
                endTime: "16:00",
                topic: "",
                agenda: "",
              },
            ],
          };
        }
        return day;
      })
    );
  };

  const updateSession = (dayId, sessionId, updates) => {
    setSchedule((prev) =>
      prev.map((day) => {
        if (day.id === dayId) {
          return {
            ...day,
            sessions: day.sessions.map((session) =>
              session.id === sessionId ? { ...session, ...updates } : session
            ),
          };
        }
        return day;
      })
    );
  };

  const removeSession = (dayId, sessionId) => {
    setSchedule((prev) =>
      prev.map((day) => {
        if (day.id === dayId) {
          return {
            ...day,
            sessions: day.sessions.filter((s) => s.id !== sessionId),
          };
        }
        return day;
      }).filter((day) => day.sessions.length > 0) // Remove days with no sessions
    );
  };

  const removeDay = (dayId) => {
    setSchedule((prev) => 
      prev.filter((day) => day.id !== dayId)
        .map((day, index) => ({ ...day, day: index + 1 })) // Renumber days
    );
  };

  // Validate a session within a day
  const validateSessionInDay = (dayIndex, sessionIndex, session) => {
    const errors = [];
    const sessionLabel = `Day ${dayIndex + 1}, Session ${sessionIndex + 1}`;
    
    if (!session.topic?.trim()) errors.push(`${sessionLabel}: Topic required`);
    if (!session.startTime) errors.push(`${sessionLabel}: Start time required`);
    if (!session.endTime) errors.push(`${sessionLabel}: End time required`);
    if (session.startTime && session.endTime && session.startTime >= session.endTime) {
      errors.push(`${sessionLabel}: End time must be after start time`);
    }
    return errors;
  };

  const hasSessionErrors = () => {
    // Validate all sessions in all days
    for (let dayIndex = 0; dayIndex < schedule.length; dayIndex++) {
      const day = schedule[dayIndex];
      if (!day.sessions || day.sessions.length === 0) return true;
      
      for (let sessionIndex = 0; sessionIndex < day.sessions.length; sessionIndex++) {
        const errors = validateSessionInDay(dayIndex, sessionIndex, day.sessions[sessionIndex]);
        if (errors.length > 0) return true;
      }
    }
    return false;
  };

  const calculateActualDays = () => {
    if (!formData.duration.startDate || !formData.duration.endDate) return 0;
    const start = new Date(formData.duration.startDate);
    const end = new Date(formData.duration.endDate);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  // ============ PAYMENT HELPERS (Backend structure) ============
  // Backend expects: bankDetails, mobilePayment.easypaisa/jazzcash, internationalPayment.paypal/wise
  
  const validatePaymentInstructionsForBackend = (paymentInstructions) => {
    if (!paymentInstructions) {
      return { isValid: false, message: "At least one payment method is required" };
    }

    const { bankDetails, mobilePayment, internationalPayment } = paymentInstructions;
    let hasValidMethod = false;

    // Check bank details
    if (bankDetails?.accountNumber?.trim()) {
      hasValidMethod = true;
    }

    // Check mobile payment (EasyPaisa, JazzCash)
    if (mobilePayment?.easypaisa?.accountNumber?.trim()) {
      hasValidMethod = true;
    }
    if (mobilePayment?.jazzcash?.accountNumber?.trim()) {
      hasValidMethod = true;
    }

    // Check international payment (PayPal, Wise)
    if (internationalPayment?.paypal?.email?.trim()) {
      hasValidMethod = true;
    }
    if (internationalPayment?.wise?.email?.trim()) {
      hasValidMethod = true;
    }

    if (!hasValidMethod) {
      return { isValid: false, message: "At least one payment method is required for paid workshops" };
    }

    return { isValid: true };
  };

  const getPaymentMethodsStatus = () => {
    const pi = formData.paymentInstructions;
    return {
      bank: !!pi.bankDetails?.accountNumber?.trim(),
      easypaisa: !!pi.mobilePayment?.easypaisa?.accountNumber?.trim(),
      jazzcash: !!pi.mobilePayment?.jazzcash?.accountNumber?.trim(),
      paypal: !!pi.internationalPayment?.paypal?.email?.trim(),
      wise: !!pi.internationalPayment?.wise?.email?.trim(),
    };
  };

  // ============ SUBMISSION - Transform data to match backend API ============
  const prepareWorkshopDataForAPI = (status) => {
    // Transform frontend data to match backend expected structure
    const apiData = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      category: formData.category.trim(),
      field: formData.field.trim(),
      format: formData.format,
      
      // NEW: Metadata fields
      language: formData.language || "English",
      difficultyLevel: formData.difficultyLevel || "all-levels",
      prerequisites: formData.prerequisites || [],
      learningOutcomes: formData.learningOutcomes || [],
      targetAudience: formData.targetAudience?.trim() || "",
      certificateOffered: formData.certificateOffered || false,
      certificateDetails: formData.certificateOffered ? formData.certificateDetails?.trim() || "" : "",
      
      // Location structure per backend
      location: {
        ...(formData.format === "online" && {
          platform: formData.location.platform || "Online",
          meetingLink: formData.location.meetingLink,
        }),
        ...(formData.format === "physical" && {
          venue: formData.location.venue,
          address: formData.location.address,
          city: formData.location.city,
          country: formData.location.country,
        }),
      },
      
      // Duration structure
      duration: {
        days: parseInt(formData.duration.days),
        startDate: formData.duration.startDate,
        endDate: formData.duration.endDate,
      },
      
      // Capacity structure
      capacity: {
        limit: formData.capacity.unlimited ? undefined : parseInt(formData.capacity.limit),
        unlimited: formData.capacity.unlimited,
      },
      
      // Pricing structure
      pricing: {
        type: formData.pricing.type,
        ...(formData.pricing.type === "paid" && {
          amount: parseFloat(formData.pricing.amount),
          currency: formData.pricing.currency || "PKR",
        }),
        ...(formData.pricing.type === "paid" && {
          paymentInstructions: formData.paymentInstructions,
        }),
      },
      
      // Schedule - already in correct format
      schedule: schedule.map(day => ({
        day: day.day,
        date: day.date,
        sessions: day.sessions.map(session => ({
          startTime: session.startTime,
          endTime: session.endTime,
          topic: session.topic,
          agenda: session.agenda || "",
        })),
      })),
      
      // Settings
      settings: {
        allowQuestions: formData.settings.allowQuestions,
        requireApproval: formData.settings.requireApproval,
        sendReminders: formData.settings.sendReminders,
        autoApprovePayments: formData.settings.autoApprovePayments,
      },
      
      // Tags and visibility
      tags: formData.tags,
      visibility: formData.visibility || "public",
      
      // Status
      status: status,
    };
    
    return apiData;
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;
    setSubmitting(true);
    try {
      const workshopData = prepareWorkshopDataForAPI("published");
      const response = isEditMode
        ? await workshopApi.updateWorkshop(workshopId, workshopData)
        : await workshopApi.createWorkshop(workshopData);
      if (response.success) {
        // Extract workshop data - backend returns response.data.workshop OR response.data directly
        const createdWorkshop = response.data?.workshop || response.data;
        
        // Validate we have a valid workshop with _id
        if (!createdWorkshop?._id) {
setErrors({ general: "Workshop created but response is invalid. Please check My Workshops." });
          return;
        }
        
        // Show success state
        setSubmitSuccess({ type: 'published', workshop: createdWorkshop });
        // Call callback if provided
        if (onSubmit) {
          setTimeout(() => onSubmit(createdWorkshop), 2000); // Give user time to see success
        }
      } else {
        setErrors({ general: response.message || "Failed to save workshop" });
      }
    } catch (error) {
      setErrors({ general: error.message || "An error occurred" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAsDraft = async () => {
    setSubmitting(true);
    try {
      const workshopData = prepareWorkshopDataForAPI("draft");
      const response = isEditMode
        ? await workshopApi.updateWorkshop(workshopId, workshopData)
        : await workshopApi.createWorkshop(workshopData);
      if (response.success) {
        // Extract workshop data - backend returns response.data.workshop OR response.data directly
        const createdWorkshop = response.data?.workshop || response.data;
        
        // Validate we have a valid workshop with _id
        if (!createdWorkshop?._id) {
// Still navigate to my-workshops since the draft was likely created
          setTimeout(() => navigate('/workshops/my-workshops'), 1500);
          return;
        }
        
        // Show success state
        setSubmitSuccess({ type: 'draft', workshop: createdWorkshop });
        // For drafts, redirect to my-workshops after delay
        setTimeout(() => {
          if (onSubmit) {
            onSubmit(createdWorkshop);
          } else {
            navigate('/workshops/my-workshops');
          }
        }, 2000);
      } else {
        setErrors({ general: response.message || "Failed to save draft" });
      }
    } catch (error) {
      setErrors({ general: error.message || "An error occurred" });
    } finally {
      setSubmitting(false);
    }
  };

  // ============ STEP DEFINITIONS ============
  const steps = [
    { id: 1, label: "Basic Info", icon: FiBookOpen },
    { id: 2, label: "Format & Time", icon: FiCalendar },
    { id: 3, label: "Pricing", icon: FiDollarSign },
    { id: 4, label: "Schedule", icon: FiClock },
    { id: 5, label: "Settings", icon: FiSettings },
  ];

  // ============ ANIMATION VARIANTS ============
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
    exit: { opacity: 0 },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const slideVariants = {
    enter: (direction) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({ x: direction > 0 ? -300 : 300, opacity: 0 }),
  };

  // ============ RENDER STEP INDICATOR ============
  const renderStepIndicator = () => (
    <div className="wcf-step-indicator">
      {steps.map((step, index) => {
        const StepIcon = step.icon;
        const isActive = currentStep === step.id;
        const isCompleted = currentStep > step.id;
        return (
          <React.Fragment key={step.id}>
            <motion.button
              type="button"
              className={`wcf-step-item ${isActive ? "active" : ""} ${
                isCompleted ? "completed" : ""
              }`}
              onClick={() => goToStep(step.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="wcf-step-icon">
                {isCompleted ? <FiCheck /> : <StepIcon />}
              </div>
              <span className="wcf-step-label">{step.label}</span>
            </motion.button>
            {index < steps.length - 1 && (
              <div
                className={`wcf-step-connector ${
                  isCompleted ? "completed" : ""
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // ============ RENDER BASIC INFO STEP ============
  const renderBasicInfoStep = () => (
    <motion.div
      className="wcf-step-content"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div className="wcf-step-header">
        <FiBookOpen className="wcf-step-header-icon" />
        <div>
          <h3>Basic Information</h3>
          <p>Tell us about your workshop</p>
        </div>
      </div>

      <motion.div className="wcf-form-group" variants={itemVariants}>
        <label className="wcf-label">
          <FiEdit3 /> Workshop Title
        </label>
        <input
          type="text"
          className={`wcf-input ${errors.title ? "error" : ""}`}
          placeholder="e.g., Introduction to Machine Learning"
          value={formData.title}
          onChange={(e) => handleInputChange("title", e.target.value)}
        />
        {errors.title && <span className="wcf-error">{errors.title}</span>}
      </motion.div>

      <motion.div className="wcf-form-group" variants={itemVariants}>
        <label className="wcf-label">
          <FiFileText /> Description
        </label>
        <textarea
          className={`wcf-textarea ${errors.description ? "error" : ""}`}
          placeholder="Describe what participants will learn..."
          rows={5}
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
        />
        <div className="wcf-char-count">{formData.description.length}/2000</div>
        {errors.description && (
          <span className="wcf-error">{errors.description}</span>
        )}
      </motion.div>

      <motion.div className="wcf-form-row" variants={itemVariants}>
        <div className="wcf-form-group">
          <label className="wcf-label">
            <FiTag /> Category
          </label>
          <select
            className={`wcf-select ${errors.category ? "error" : ""}`}
            value={formData.category}
            onChange={(e) => handleInputChange("category", e.target.value)}
          >
            <option value="">Select category</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          {errors.category && (
            <span className="wcf-error">{errors.category}</span>
          )}
        </div>

        <div className="wcf-form-group">
          <label className="wcf-label">
            <FiBookOpen /> Research Field <span className="wcf-required">*</span>
          </label>
          <select
            className={`wcf-select ${errors.field ? "error" : ""}`}
            value={formData.field}
            onChange={(e) => handleInputChange("field", e.target.value)}
          >
            <option value="">Select field (required)</option>
            {researchFields.map((field) => (
              <option key={field} value={field}>
                {field}
              </option>
            ))}
          </select>
          {errors.field && (
            <span className="wcf-error">{errors.field}</span>
          )}
        </div>
      </motion.div>

      <motion.div className="wcf-form-group" variants={itemVariants}>
        <label className="wcf-label">
          <FiTag /> Tags
        </label>
        <div className="wcf-tags-input">
          <div className="wcf-tags-list">
            {formData.tags.map((tag) => (
              <span key={tag} className="wcf-tag">
                {tag}
                <button type="button" onClick={() => handleTagRemove(tag)}>
                  <FiX />
                </button>
              </span>
            ))}
          </div>
          <div className="wcf-tag-add">
            <input
              type="text"
              placeholder="Add a tag..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) =>
                e.key === "Enter" && (e.preventDefault(), handleTagAdd())
              }
            />
            <button type="button" onClick={handleTagAdd}>
              <FiPlus />
            </button>
          </div>
        </div>
      </motion.div>

      {/* NEW: Language & Difficulty Level */}
      <motion.div className="wcf-form-row" variants={itemVariants}>
        <div className="wcf-form-group">
          <label className="wcf-label">
            <FiGlobe /> Language
          </label>
          <select
            className="wcf-select"
            value={formData.language}
            onChange={(e) => handleInputChange("language", e.target.value)}
          >
            <option value="English">English</option>
            <option value="Urdu">Urdu</option>
            <option value="Hindi">Hindi</option>
            <option value="Arabic">Arabic</option>
            <option value="Spanish">Spanish</option>
            <option value="French">French</option>
            <option value="German">German</option>
            <option value="Chinese">Chinese</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="wcf-form-group">
          <label className="wcf-label">
            <FiBookOpen /> Difficulty Level
          </label>
          <select
            className="wcf-select"
            value={formData.difficultyLevel}
            onChange={(e) => handleInputChange("difficultyLevel", e.target.value)}
          >
            <option value="beginner">🌱 Beginner - No prior experience needed</option>
            <option value="intermediate">📈 Intermediate - Some experience recommended</option>
            <option value="advanced">🚀 Advanced - Strong foundation required</option>
            <option value="all-levels">👥 All Levels - Suitable for everyone</option>
          </select>
        </div>
      </motion.div>

      {/* NEW: Learning Outcomes */}
      <motion.div className="wcf-form-group" variants={itemVariants}>
        <label className="wcf-label">
          <FiCheck /> What Will Participants Learn?
        </label>
        <p className="wcf-label-hint">Add specific learning outcomes (recommended)</p>
        <div className="wcf-tags-input">
          <div className="wcf-tags-list wcf-outcomes-list">
            {formData.learningOutcomes.map((outcome, idx) => (
              <span key={idx} className="wcf-tag wcf-outcome-tag">
                ✅ {outcome}
                <button type="button" onClick={() => {
                  const newOutcomes = formData.learningOutcomes.filter((_, i) => i !== idx);
                  handleInputChange("learningOutcomes", newOutcomes);
                }}>
                  <FiX />
                </button>
              </span>
            ))}
          </div>
          <div className="wcf-tag-add">
            <input
              type="text"
              placeholder="e.g., Understand the basics of machine learning..."
              value={outcomeInput}
              onChange={(e) => setOutcomeInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (outcomeInput.trim() && formData.learningOutcomes.length < 10) {
                    handleInputChange("learningOutcomes", [...formData.learningOutcomes, outcomeInput.trim()]);
                    setOutcomeInput("");
                  }
                }
              }}
            />
            <button type="button" onClick={() => {
              if (outcomeInput.trim() && formData.learningOutcomes.length < 10) {
                handleInputChange("learningOutcomes", [...formData.learningOutcomes, outcomeInput.trim()]);
                setOutcomeInput("");
              }
            }}>
              <FiPlus />
            </button>
          </div>
        </div>
      </motion.div>

      {/* NEW: Prerequisites */}
      <motion.div className="wcf-form-group" variants={itemVariants}>
        <label className="wcf-label">
          <FiInfo /> Prerequisites (Optional)
        </label>
        <p className="wcf-label-hint">What should participants know beforehand?</p>
        <div className="wcf-tags-input">
          <div className="wcf-tags-list">
            {formData.prerequisites.map((prereq, idx) => (
              <span key={idx} className="wcf-tag">
                {prereq}
                <button type="button" onClick={() => {
                  const newPrereqs = formData.prerequisites.filter((_, i) => i !== idx);
                  handleInputChange("prerequisites", newPrereqs);
                }}>
                  <FiX />
                </button>
              </span>
            ))}
          </div>
          <div className="wcf-tag-add">
            <input
              type="text"
              placeholder="e.g., Basic Python knowledge..."
              value={prerequisiteInput}
              onChange={(e) => setPrerequisiteInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (prerequisiteInput.trim() && formData.prerequisites.length < 10) {
                    handleInputChange("prerequisites", [...formData.prerequisites, prerequisiteInput.trim()]);
                    setPrerequisiteInput("");
                  }
                }
              }}
            />
            <button type="button" onClick={() => {
              if (prerequisiteInput.trim() && formData.prerequisites.length < 10) {
                handleInputChange("prerequisites", [...formData.prerequisites, prerequisiteInput.trim()]);
                setPrerequisiteInput("");
              }
            }}>
              <FiPlus />
            </button>
          </div>
        </div>
      </motion.div>

      {/* NEW: Target Audience */}
      <motion.div className="wcf-form-group" variants={itemVariants}>
        <label className="wcf-label">
          <FiUsers /> Target Audience (Optional)
        </label>
        <textarea
          className="wcf-textarea"
          placeholder="Describe who should attend this workshop..."
          rows={2}
          value={formData.targetAudience}
          onChange={(e) => handleInputChange("targetAudience", e.target.value)}
        />
      </motion.div>

      {/* NEW: Certificate */}
      <motion.div className="wcf-form-group" variants={itemVariants}>
        <label className="wcf-label">
          <FiAward /> Certificate
        </label>
        <div className="wcf-checkbox-group">
          <label className="wcf-checkbox">
            <input
              type="checkbox"
              checked={formData.certificateOffered}
              onChange={(e) => handleInputChange("certificateOffered", e.target.checked)}
            />
            <span className="wcf-checkbox-mark"></span>
            <span>Offer certificate upon completion</span>
          </label>
        </div>
        {formData.certificateOffered && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <textarea
              className="wcf-textarea wcf-textarea-sm"
              placeholder="Certificate details (optional) - e.g., 'Certificate of completion from ResearchPro'"
              rows={2}
              value={formData.certificateDetails}
              onChange={(e) => handleInputChange("certificateDetails", e.target.value)}
            />
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );

  // ============ RENDER SCHEDULE STEP (Format & Time) ============
  const renderScheduleStep = () => (
    <motion.div
      className="wcf-step-content"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div className="wcf-step-header">
        <FiCalendar className="wcf-step-header-icon" />
        <div>
          <h3>Format & Timing</h3>
          <p>Set up when and how your workshop will be conducted</p>
        </div>
      </div>

      <motion.div className="wcf-form-group" variants={itemVariants}>
        <label className="wcf-label">
          <FiGlobe /> Workshop Format
        </label>
        <div className="wcf-format-options">
          <button
            type="button"
            className={`wcf-format-option ${
              formData.format === "online" ? "active" : ""
            }`}
            onClick={() => handleInputChange("format", "online")}
          >
            <FiGlobe />
            <span>Online</span>
          </button>
          <button
            type="button"
            className={`wcf-format-option ${
              formData.format === "physical" ? "active" : ""
            }`}
            onClick={() => handleInputChange("format", "physical")}
          >
            <FiMapPin />
            <span>In-Person</span>
          </button>
        </div>
      </motion.div>

      {formData.format === "online" && (
        <>
          <motion.div className="wcf-form-group" variants={itemVariants}>
            <label className="wcf-label">
              <FiGlobe /> Platform
            </label>
            <input
              type="text"
              className="wcf-input"
              placeholder="e.g., Zoom, Google Meet, Teams"
              value={formData.location.platform}
              onChange={(e) => handleInputChange("location.platform", e.target.value)}
            />
          </motion.div>
          <motion.div className="wcf-form-group" variants={itemVariants}>
            <label className="wcf-label">
              <FiGlobe /> Meeting Link <span className="wcf-required">*</span>
            </label>
            <input
              type="url"
              className={`wcf-input ${errors.meetingLink ? "error" : ""}`}
              placeholder="https://zoom.us/j/..."
              value={formData.location.meetingLink}
              onChange={(e) => handleInputChange("location.meetingLink", e.target.value)}
            />
            {errors.meetingLink && (
              <span className="wcf-error">{errors.meetingLink}</span>
            )}
          </motion.div>
        </>
      )}

      {formData.format === "physical" && (
        <>
          <motion.div className="wcf-form-group" variants={itemVariants}>
            <label className="wcf-label">
              <FiMapPin /> Venue <span className="wcf-required">*</span>
            </label>
            <input
              type="text"
              className={`wcf-input ${errors.venue ? "error" : ""}`}
              placeholder="Enter venue name"
              value={formData.location.venue}
              onChange={(e) => handleInputChange("location.venue", e.target.value)}
            />
            {errors.venue && (
              <span className="wcf-error">{errors.venue}</span>
            )}
          </motion.div>
          <motion.div className="wcf-form-group" variants={itemVariants}>
            <label className="wcf-label">
              <FiMapPin /> Address
            </label>
            <input
              type="text"
              className="wcf-input"
              placeholder="Enter full address"
              value={formData.location.address}
              onChange={(e) => handleInputChange("location.address", e.target.value)}
            />
          </motion.div>
          <motion.div className="wcf-form-row" variants={itemVariants}>
            <div className="wcf-form-group">
              <label className="wcf-label">City</label>
              <input
                type="text"
                className="wcf-input"
                placeholder="City"
                value={formData.location.city}
                onChange={(e) => handleInputChange("location.city", e.target.value)}
              />
            </div>
            <div className="wcf-form-group">
              <label className="wcf-label">Country</label>
              <input
                type="text"
                className="wcf-input"
                placeholder="Country"
                value={formData.location.country}
                onChange={(e) => handleInputChange("location.country", e.target.value)}
              />
            </div>
          </motion.div>
        </>
      )}

      <motion.div className="wcf-form-group" variants={itemVariants}>
        <label className="wcf-label">
          <FiClock /> Duration (Days) <span className="wcf-required">*</span>
        </label>
        <input
          type="number"
          className={`wcf-input ${errors.days ? "error" : ""}`}
          min="1"
          max="30"
          value={formData.duration.days}
          onChange={(e) => handleInputChange("duration.days", parseInt(e.target.value) || 1)}
        />
        {errors.days && <span className="wcf-error">{errors.days}</span>}
        <span className="wcf-helper">Maximum 30 days</span>
      </motion.div>

      <motion.div className="wcf-form-row" variants={itemVariants}>
        <div className="wcf-form-group">
          <label className="wcf-label">
            <FiCalendar /> Start Date <span className="wcf-required">*</span>
          </label>
          <input
            type="date"
            className={`wcf-input ${errors.startDate ? "error" : ""}`}
            value={formData.duration.startDate}
            min={new Date().toISOString().split('T')[0]}
            onChange={(e) => handleInputChange("duration.startDate", e.target.value)}
          />
          {errors.startDate && (
            <span className="wcf-error">{errors.startDate}</span>
          )}
        </div>
        <div className="wcf-form-group">
          <label className="wcf-label">
            <FiCalendar /> End Date
          </label>
          <input
            type="date"
            className="wcf-input"
            value={formData.duration.endDate}
            disabled
          />
          <span className="wcf-helper">Auto-calculated based on duration</span>
        </div>
      </motion.div>

      <motion.div className="wcf-info-box" variants={itemVariants}>
        <FiInfo />
        <p>
          Your workshop will run for{" "}
          <strong>{formData.duration.days} day(s)</strong>
          {formData.duration.startDate && formData.duration.endDate && (
            <span>
              {" "}from <strong>{new Date(formData.duration.startDate).toLocaleDateString()}</strong> to{" "}
              <strong>{new Date(formData.duration.endDate).toLocaleDateString()}</strong>
            </span>
          )}
        </p>
      </motion.div>
    </motion.div>
  );

  // ============ RENDER PRICING STEP ============
  const renderPricingStep = () => {
    const paymentStatus = getPaymentMethodsStatus();
    return (
      <motion.div
        className="wcf-step-content"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <div className="wcf-step-header">
          <FiDollarSign className="wcf-step-header-icon" />
          <div>
            <h3>Pricing & Capacity</h3>
            <p>Set your workshop price and participant limits</p>
          </div>
        </div>

        {/* Capacity Section */}
        <motion.div className="wcf-form-group" variants={itemVariants}>
          <label className="wcf-checkbox-inline">
            <input
              type="checkbox"
              checked={formData.capacity.unlimited}
              onChange={(e) => handleInputChange("capacity.unlimited", e.target.checked)}
            />
            <span>Unlimited Capacity</span>
          </label>
        </motion.div>

        {!formData.capacity.unlimited && (
          <motion.div className="wcf-form-group" variants={itemVariants}>
            <label className="wcf-label">
              <FiUsers /> Maximum Participants
            </label>
            <input
              type="number"
              className={`wcf-input ${errors.capacity ? "error" : ""}`}
              min="1"
              max="1000"
              value={formData.capacity.limit}
              onChange={(e) =>
                handleInputChange("capacity.limit", parseInt(e.target.value) || 1)
              }
            />
            {errors.capacity && (
              <span className="wcf-error">{errors.capacity}</span>
            )}
            <span className="wcf-helper">Maximum 1000 participants</span>
          </motion.div>
        )}

        <motion.div className="wcf-form-group" variants={itemVariants}>
          <label className="wcf-label">
            <FiDollarSign /> Pricing Type
          </label>
          <div className="wcf-pricing-options">
            <button
              type="button"
              className={`wcf-pricing-option ${
                formData.pricing.type === "free" ? "active" : ""
              }`}
              onClick={() => handleInputChange("pricing.type", "free")}
            >
              <FiCheck />
              <span>Free</span>
            </button>
            <button
              type="button"
              className={`wcf-pricing-option ${
                formData.pricing.type === "paid" ? "active" : ""
              }`}
              onClick={() => handleInputChange("pricing.type", "paid")}
            >
              <FiDollarSign />
              <span>Paid</span>
            </button>
          </div>
        </motion.div>

        {formData.pricing.type === "paid" && (
          <>
            <motion.div className="wcf-form-row" variants={itemVariants}>
              <div className="wcf-form-group">
                <label className="wcf-label">
                  <FiDollarSign /> Price <span className="wcf-required">*</span>
                </label>
                <input
                  type="number"
                  className={`wcf-input ${errors.pricingAmount ? "error" : ""}`}
                  min="1"
                  max="100000"
                  value={formData.pricing.amount}
                  onChange={(e) =>
                    handleInputChange("pricing.amount", parseFloat(e.target.value) || 0)
                  }
                />
                {errors.pricingAmount && (
                  <span className="wcf-error">{errors.pricingAmount}</span>
                )}
              </div>
              <div className="wcf-form-group">
                <label className="wcf-label">Currency</label>
                <select
                  className="wcf-select"
                  value={formData.pricing.currency}
                  onChange={(e) => handleInputChange("pricing.currency", e.target.value)}
                >
                  <option value="PKR">PKR (Pakistani Rupee)</option>
                  <option value="USD">USD (US Dollar)</option>
                  <option value="EUR">EUR (Euro)</option>
                  <option value="GBP">GBP (British Pound)</option>
                </select>
              </div>
            </motion.div>

            <motion.div className="wcf-payment-section" variants={itemVariants}>
              <h4>
                <FiCreditCard /> Payment Methods
              </h4>
              <p className="wcf-payment-subtitle">
                Configure at least one payment method
              </p>
              {errors.payment && (
                <span className="wcf-error">{errors.payment}</span>
              )}

              {/* Bank Transfer - Backend: paymentInstructions.bankDetails */}
              <div className={`wcf-payment-method ${paymentStatus.bank ? "active" : ""}`}>
                <div className="wcf-payment-method-header">
                  <FiCreditCard /> Bank Transfer
                  {paymentStatus.bank && (
                    <span className="wcf-payment-status complete">
                      <FiCheckCircle /> Complete
                    </span>
                  )}
                </div>
                <div className="wcf-payment-fields">
                  <input
                    type="text"
                    className="wcf-input"
                    placeholder="Bank Name"
                    value={formData.paymentInstructions.bankDetails?.bankName || ""}
                    onChange={(e) =>
                      handleInputChange("paymentInstructions.bankDetails.bankName", e.target.value)
                    }
                  />
                  <input
                    type="text"
                    className="wcf-input"
                    placeholder="Account Title"
                    value={formData.paymentInstructions.bankDetails?.accountTitle || ""}
                    onChange={(e) =>
                      handleInputChange("paymentInstructions.bankDetails.accountTitle", e.target.value)
                    }
                  />
                  <input
                    type="text"
                    className="wcf-input"
                    placeholder="Account Number"
                    value={formData.paymentInstructions.bankDetails?.accountNumber || ""}
                    onChange={(e) =>
                      handleInputChange("paymentInstructions.bankDetails.accountNumber", e.target.value)
                    }
                  />
                  <input
                    type="text"
                    className="wcf-input"
                    placeholder="IBAN (optional)"
                    value={formData.paymentInstructions.bankDetails?.iban || ""}
                    onChange={(e) =>
                      handleInputChange("paymentInstructions.bankDetails.iban", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* EasyPaisa - Backend: paymentInstructions.mobilePayment.easypaisa */}
              <div className={`wcf-payment-method ${paymentStatus.easypaisa ? "active" : ""}`}>
                <div className="wcf-payment-method-header">
                  <FiSmartphone /> EasyPaisa
                  {paymentStatus.easypaisa && (
                    <span className="wcf-payment-status complete">
                      <FiCheckCircle /> Complete
                    </span>
                  )}
                </div>
                <div className="wcf-payment-fields">
                  <input
                    type="text"
                    className="wcf-input"
                    placeholder="Account Number (03XX-XXXXXXX)"
                    value={formData.paymentInstructions.mobilePayment?.easypaisa?.accountNumber || ""}
                    onChange={(e) =>
                      handleInputChange("paymentInstructions.mobilePayment.easypaisa.accountNumber", e.target.value)
                    }
                  />
                  <input
                    type="text"
                    className="wcf-input"
                    placeholder="Account Title"
                    value={formData.paymentInstructions.mobilePayment?.easypaisa?.accountTitle || ""}
                    onChange={(e) =>
                      handleInputChange("paymentInstructions.mobilePayment.easypaisa.accountTitle", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* JazzCash - Backend: paymentInstructions.mobilePayment.jazzcash */}
              <div className={`wcf-payment-method ${paymentStatus.jazzcash ? "active" : ""}`}>
                <div className="wcf-payment-method-header">
                  <FiSmartphone /> JazzCash
                  {paymentStatus.jazzcash && (
                    <span className="wcf-payment-status complete">
                      <FiCheckCircle /> Complete
                    </span>
                  )}
                </div>
                <div className="wcf-payment-fields">
                  <input
                    type="text"
                    className="wcf-input"
                    placeholder="Account Number (03XX-XXXXXXX)"
                    value={formData.paymentInstructions.mobilePayment?.jazzcash?.accountNumber || ""}
                    onChange={(e) =>
                      handleInputChange("paymentInstructions.mobilePayment.jazzcash.accountNumber", e.target.value)
                    }
                  />
                  <input
                    type="text"
                    className="wcf-input"
                    placeholder="Account Title"
                    value={formData.paymentInstructions.mobilePayment?.jazzcash?.accountTitle || ""}
                    onChange={(e) =>
                      handleInputChange("paymentInstructions.mobilePayment.jazzcash.accountTitle", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* PayPal - Backend: paymentInstructions.internationalPayment.paypal */}
              <div className={`wcf-payment-method ${paymentStatus.paypal ? "active" : ""}`}>
                <div className="wcf-payment-method-header">
                  <FiMail /> PayPal
                  {paymentStatus.paypal && (
                    <span className="wcf-payment-status complete">
                      <FiCheckCircle /> Complete
                    </span>
                  )}
                </div>
                <div className="wcf-payment-fields">
                  <input
                    type="email"
                    className="wcf-input"
                    placeholder="PayPal Email"
                    value={formData.paymentInstructions.internationalPayment?.paypal?.email || ""}
                    onChange={(e) =>
                      handleInputChange("paymentInstructions.internationalPayment.paypal.email", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Wise - Backend: paymentInstructions.internationalPayment.wise */}
              <div className={`wcf-payment-method ${paymentStatus.wise ? "active" : ""}`}>
                <div className="wcf-payment-method-header">
                  <FiGlobe /> Wise
                  {paymentStatus.wise && (
                    <span className="wcf-payment-status complete">
                      <FiCheckCircle /> Complete
                    </span>
                  )}
                </div>
                <div className="wcf-payment-fields">
                  <input
                    type="email"
                    className="wcf-input"
                    placeholder="Wise Email"
                    value={formData.paymentInstructions.internationalPayment?.wise?.email || ""}
                    onChange={(e) =>
                      handleInputChange("paymentInstructions.internationalPayment.wise.email", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Contact Info - Backend: paymentInstructions.contactInfo */}
              <div className="wcf-payment-method">
                <div className="wcf-payment-method-header">
                  <FiMessageCircle /> Contact Info
                </div>
                <div className="wcf-payment-fields">
                  <input
                    type="email"
                    className="wcf-input"
                    placeholder="Contact Email"
                    value={formData.paymentInstructions.contactInfo?.email || ""}
                    onChange={(e) =>
                      handleInputChange("paymentInstructions.contactInfo.email", e.target.value)
                    }
                  />
                  <input
                    type="tel"
                    className="wcf-input"
                    placeholder="WhatsApp Number"
                    value={formData.paymentInstructions.contactInfo?.whatsapp || ""}
                    onChange={(e) =>
                      handleInputChange("paymentInstructions.contactInfo.whatsapp", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Additional Instructions */}
              <div className="wcf-payment-method">
                <div className="wcf-payment-method-header">
                  <FiFileText /> Additional Instructions
                </div>
                <div className="wcf-payment-fields">
                  <textarea
                    className="wcf-textarea"
                    placeholder="Any additional payment instructions for participants (max 1000 chars)"
                    rows={3}
                    maxLength={1000}
                    value={formData.paymentInstructions.additionalInstructions || ""}
                    onChange={(e) =>
                      handleInputChange("paymentInstructions.additionalInstructions", e.target.value)
                    }
                  />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </motion.div>
    );
  };

  // ============ RENDER SCHEDULE DETAILS STEP (NEW Backend format) ============
  const renderScheduleDetailsStep = () => (
    <motion.div
      className="wcf-step-content"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div className="wcf-step-header">
        <FiClock className="wcf-step-header-icon" />
        <div>
          <h3>Session Schedule</h3>
          <p>Plan your workshop sessions day by day</p>
        </div>
      </div>

      {formData.duration.startDate && formData.duration.days ? (
        <>
          <motion.div className="wcf-schedule-info" variants={itemVariants}>
            <div className="wcf-schedule-meta">
              <span>
                <FiCalendar /> {formData.duration.startDate} - {formData.duration.endDate}
              </span>
              <span>
                <FiClock /> {formData.duration.days} days
              </span>
            </div>
            {schedule.length === 0 && (
              <button
                type="button"
                className="wcf-btn wcf-btn-primary"
                onClick={generateScheduleTemplate}
              >
                <FiPlus /> Generate Schedule
              </button>
            )}
          </motion.div>

          {errors.schedule && (
            <span className="wcf-error">{errors.schedule}</span>
          )}

          {/* Schedule days - Backend format: schedule[].sessions[] */}
          <AnimatePresence mode="popLayout">
            {schedule.map((day, dayIndex) => (
              <motion.div
                key={day.id}
                className="wcf-day-card"
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0, x: -100 }}
                layout
              >
                <div className="wcf-day-header">
                  <div className="wcf-day-info">
                    <span className="wcf-day-number">Day {day.day}</span>
                    <span className="wcf-day-date">
                      {new Date(day.date).toLocaleDateString('en-US', { 
                        weekday: 'short', month: 'short', day: 'numeric' 
                      })}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="wcf-btn-icon wcf-btn-danger"
                    onClick={() => removeDay(day.id)}
                    title="Remove Day"
                  >
                    <FiTrash2 />
                  </button>
                </div>

                {/* Sessions within this day */}
                <div className="wcf-sessions-list">
                  {day.sessions.map((session, sessionIndex) => (
                    <div key={session.id} className="wcf-session-item">
                      <div className="wcf-session-header-inner">
                        <span className="wcf-session-number">Session {sessionIndex + 1}</span>
                        {day.sessions.length > 1 && (
                          <button
                            type="button"
                            className="wcf-btn-icon wcf-btn-sm wcf-btn-danger"
                            onClick={() => removeSession(day.id, session.id)}
                          >
                            <FiX />
                          </button>
                        )}
                      </div>
                      
                      <div className="wcf-form-row">
                        <div className="wcf-form-group">
                          <label className="wcf-label">Start Time</label>
                          <input
                            type="time"
                            className="wcf-input"
                            value={session.startTime}
                            onChange={(e) =>
                              updateSession(day.id, session.id, { startTime: e.target.value })
                            }
                          />
                        </div>
                        <div className="wcf-form-group">
                          <label className="wcf-label">End Time</label>
                          <input
                            type="time"
                            className="wcf-input"
                            value={session.endTime}
                            onChange={(e) =>
                              updateSession(day.id, session.id, { endTime: e.target.value })
                            }
                          />
                        </div>
                      </div>

                      <div className="wcf-form-group">
                        <label className="wcf-label">Topic <span className="wcf-required">*</span></label>
                        <input
                          type="text"
                          className="wcf-input"
                          placeholder="Session topic (1-200 chars)"
                          maxLength={200}
                          value={session.topic}
                          onChange={(e) =>
                            updateSession(day.id, session.id, { topic: e.target.value })
                          }
                        />
                      </div>

                      <div className="wcf-form-group">
                        <label className="wcf-label">Agenda (optional)</label>
                        <textarea
                          className="wcf-textarea"
                          rows={2}
                          placeholder="What will be covered in this session? (max 1000 chars)"
                          maxLength={1000}
                          value={session.agenda || ""}
                          onChange={(e) =>
                            updateSession(day.id, session.id, { agenda: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    className="wcf-btn wcf-btn-outline wcf-btn-sm"
                    onClick={() => addSessionToDay(day.id)}
                  >
                    <FiPlus /> Add Session to Day {day.day}
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {schedule.length > 0 && (
            <motion.button
              type="button"
              className="wcf-btn wcf-btn-outline wcf-add-day"
              onClick={addDayToSchedule}
              variants={itemVariants}
            >
              <FiPlus /> Add Another Day
            </motion.button>
          )}
        </>
      ) : (
        <motion.div className="wcf-empty-schedule" variants={itemVariants}>
          <FiAlertCircle />
          <p>
            Please complete the duration and start date in the previous step to
            create a schedule.
          </p>
        </motion.div>
      )}
    </motion.div>
  );

  // ============ RENDER SETTINGS STEP (Backend fields) ============
  const renderSettingsStep = () => (
    <motion.div
      className="wcf-step-content"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div className="wcf-step-header">
        <FiSettings className="wcf-step-header-icon" />
        <div>
          <h3>Workshop Settings</h3>
          <p>Configure additional options for your workshop</p>
        </div>
      </div>

      <motion.div className="wcf-settings-grid" variants={containerVariants}>
        <motion.div className="wcf-setting-item" variants={itemVariants}>
          <label className="wcf-toggle">
            <input
              type="checkbox"
              checked={formData.settings.allowQuestions}
              onChange={(e) =>
                handleInputChange("settings.allowQuestions", e.target.checked)
              }
            />
            <span className="wcf-toggle-slider"></span>
            <div className="wcf-toggle-content">
              <FiHelpCircle />
              <div>
                <span className="wcf-toggle-title">Allow Questions</span>
                <span className="wcf-toggle-desc">
                  Participants can ask questions during the workshop
                </span>
              </div>
            </div>
          </label>
        </motion.div>

        <motion.div className="wcf-setting-item" variants={itemVariants}>
          <label className="wcf-toggle">
            <input
              type="checkbox"
              checked={formData.settings.requireApproval}
              onChange={(e) =>
                handleInputChange("settings.requireApproval", e.target.checked)
              }
            />
            <span className="wcf-toggle-slider"></span>
            <div className="wcf-toggle-content">
              <FiCheck />
              <div>
                <span className="wcf-toggle-title">Require Approval</span>
                <span className="wcf-toggle-desc">
                  Manually approve each participant before they can join (recommended)
                </span>
              </div>
            </div>
          </label>
        </motion.div>

        <motion.div className="wcf-setting-item" variants={itemVariants}>
          <label className="wcf-toggle">
            <input
              type="checkbox"
              checked={formData.settings.sendReminders}
              onChange={(e) =>
                handleInputChange("settings.sendReminders", e.target.checked)
              }
            />
            <span className="wcf-toggle-slider"></span>
            <div className="wcf-toggle-content">
              <FiBell />
              <div>
                <span className="wcf-toggle-title">Send Reminders</span>
                <span className="wcf-toggle-desc">
                  Automatic reminder notifications before sessions
                </span>
              </div>
            </div>
          </label>
        </motion.div>

        {formData.pricing.type === "paid" && (
          <motion.div className="wcf-setting-item" variants={itemVariants}>
            <label className="wcf-toggle">
              <input
                type="checkbox"
                checked={formData.settings.autoApprovePayments}
                onChange={(e) =>
                  handleInputChange("settings.autoApprovePayments", e.target.checked)
                }
              />
              <span className="wcf-toggle-slider"></span>
              <div className="wcf-toggle-content">
                <FiCreditCard />
                <div>
                  <span className="wcf-toggle-title">Auto-Approve Payments</span>
                  <span className="wcf-toggle-desc">
                    Automatically approve participants when they upload payment proof
                  </span>
                </div>
              </div>
            </label>
          </motion.div>
        )}
      </motion.div>

      {/* Validation Summary */}
      <motion.div className="wcf-validation-summary" variants={itemVariants}>
        <h4>Pre-submit Checklist</h4>
        <div className="wcf-validation-items">
          <div
            className={`wcf-validation-item ${
              formData.title && formData.field ? "success" : "pending"
            }`}
          >
            {formData.title && formData.field ? <FiCheckCircle /> : <FiAlertCircle />}
            <span>Basic information complete</span>
          </div>
          <div
            className={`wcf-validation-item ${
              formData.duration.startDate ? "success" : "pending"
            }`}
          >
            {formData.duration.startDate ? <FiCheckCircle /> : <FiAlertCircle />}
            <span>Schedule timing configured</span>
          </div>
          <div
            className={`wcf-validation-item ${
              formData.pricing.type === "free" ||
              validatePaymentInstructionsForBackend(formData.paymentInstructions).isValid
                ? "success"
                : "pending"
            }`}
          >
            {formData.pricing.type === "free" ||
            validatePaymentInstructionsForBackend(formData.paymentInstructions)
              .isValid ? (
              <FiCheckCircle />
            ) : (
              <FiAlertCircle />
            )}
            <span>Pricing & payment setup</span>
          </div>
          <div
            className={`wcf-validation-item ${
              schedule.length > 0 && !hasSessionErrors() ? "success" : "pending"
            }`}
          >
            {schedule.length > 0 && !hasSessionErrors() ? (
              <FiCheckCircle />
            ) : (
              <FiAlertCircle />
            )}
            <span>Session schedule validated</span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  // ============ LOADING STATE ============
  if (loading) {
    return (
      <div className="wcf-loading">
        <FiLoader className="wcf-spinner" />
        <p>Loading workshop data...</p>
      </div>
    );
  }

  // ============ MAIN RENDER ============
  
  // Show success screen after submission
  if (submitSuccess) {
    return (
      <div className="wcf-container">
        <motion.div 
          className="wcf-success-screen"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="wcf-success-icon">
            <FiCheck size={48} />
          </div>
          <h2>
            {submitSuccess.type === 'published' 
              ? '🎉 Workshop Published!' 
              : '📝 Draft Saved!'}
          </h2>
          <p>
            {submitSuccess.type === 'published'
              ? 'Your workshop is now live and visible to the community.'
              : 'Your workshop has been saved as a draft. You can publish it later from My Workshops.'}
          </p>
          <div className="wcf-success-actions">
            <button
              className="wcf-btn wcf-btn-primary"
              onClick={() => navigate(`/workshops/${submitSuccess.workshop._id}`)}
            >
              View Workshop
            </button>
            <button
              className="wcf-btn wcf-btn-outline"
              onClick={() => navigate('/workshops/my-workshops')}
            >
              Go to My Workshops
            </button>
          </div>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="wcf-container">
      <div className="wcf-header">
        <div className="wcf-header-content">
          <h1>{isEditMode ? "Edit Workshop" : "Create Workshop"}</h1>
          <p>
            {isEditMode
              ? "Update your workshop details"
              : "Share your expertise with the community"}
          </p>
        </div>
      </div>

      {renderStepIndicator()}

      <form className="wcf-form" onSubmit={(e) => e.preventDefault()}>
        <AnimatePresence mode="wait">
          {currentStep === 1 && renderBasicInfoStep()}
          {currentStep === 2 && renderScheduleStep()}
          {currentStep === 3 && renderPricingStep()}
          {currentStep === 4 && renderScheduleDetailsStep()}
          {currentStep === 5 && renderSettingsStep()}
        </AnimatePresence>

        {errors.general && (
          <motion.div
            className="wcf-error-banner"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FiAlertCircle />
            <p>{errors.general}</p>
          </motion.div>
        )}

        <div className="wcf-navigation">
          <div className="wcf-nav-left">
            {currentStep > 1 && (
              <motion.button
                type="button"
                className="wcf-btn wcf-btn-outline"
                onClick={prevStep}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiChevronLeft /> Previous
              </motion.button>
            )}
            {onCancel && (
              <motion.button
                type="button"
                className="wcf-btn wcf-btn-ghost"
                onClick={onCancel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiX /> Cancel
              </motion.button>
            )}
          </div>

          <div className="wcf-nav-right">
            {currentStep < totalSteps ? (
              <motion.button
                type="button"
                className="wcf-btn wcf-btn-primary"
                onClick={nextStep}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Next <FiChevronRight />
              </motion.button>
            ) : (
              <>
                <motion.button
                  type="button"
                  className="wcf-btn wcf-btn-outline"
                  onClick={handleSaveAsDraft}
                  disabled={submitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {submitting ? (
                    <FiLoader className="wcf-spinner" />
                  ) : (
                    <FiSave />
                  )}
                  {isEditMode ? "Save Changes" : "Save Draft"}
                </motion.button>
                <motion.button
                  type="button"
                  className="wcf-btn wcf-btn-primary"
                  onClick={handleSubmit}
                  disabled={
                    submitting ||
                    hasSessionErrors() ||
                    (formData.pricing.type === "paid" &&
                      !validatePaymentInstructionsForBackend(formData.paymentInstructions)
                        .isValid)
                  }
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {submitting ? (
                    <FiLoader className="wcf-spinner" />
                  ) : (
                    <FiCheck />
                  )}
                  {isEditMode ? "Update Workshop" : "Create Workshop"}
                </motion.button>
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default WorkshopCreationForm;
